import * as React from 'react';
import styles from './index.less';
import FComponentsLib from '@freelog/components-lib';
import { Space } from 'antd';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FTooltip from '@/components/FTooltip';
import fResourceOptionEditor from '@/components/fResourceOptionEditor';
import FResourceOptions from '@/components/FResourceOptions';
import * as AHooks from 'ahooks';
import fMessage from '@/components/fMessage';
// import fPolicyBuilder from '@/components/fPolicyBuilder';
import FMicroAPP_Authorization from '@/components/FMicroAPP_Authorization';
import FHotspotTooltip, { fSetHotspotTooltipVisible } from '@/components/FHotspotTooltip';
import fEditLabelsDrawer from '@/components/fEditLabelsDrawer';
import ResourceLabelsCard from '@/components/FResourceLabelsCard';
import { FRegExpMgr } from '@/utils/FRegExpMgr';
import FIcons from '@/components/FIcons';
import FCoverImageEditor from '@/components/FCoverImageEditor';
import FAttrsAndConfigs, { IAttrsAndConfigsItem } from '@/components/FAttrsAndConfigs';
import fResourcePropertyEditor3 from '@/components/fResourcePropertyEditor3';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';
import fPolicyBuilder3 from '@/components/fPolicyBuilder3';

interface CardProps {
  order: number;
  username: string;
  resourceType: string[];
  resourceTypeCode?: string;
  info: {
    uid: string;
    fileName: string;
    sha1: string;
    cover: string;
    resourceName: string;
    resourceName_optimized: string;
    resourceNameError: string;
    resourceTitle: string;
    resourceTitleError: string;
    resourceLabels: string[];
    resourcePolicies: {
      title: string;
      text: string;
    }[];
    showMore: boolean;
    systemProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
      type: 'raw' | 'additional';
      valueConfig?: {
        text?: {
          nullable: boolean;
          minLength: number;
          maxLength: number;
        };
        textArea?: {
          nullable: boolean;
          minLength: number;
          maxLength: number;
        };
        integer?: {
          nullable: boolean;
          min: number;
          max: number;
        };
        decimal?: {
          nullable: boolean;
          minDecimal: number;
          maxDecimal: number;
          precision: number;
        };
        date?: {
          nullable: boolean;
          startDate: string;
          limitDate: string;
        };
        dataTime?: {
          nullable: boolean;
          startDateTime: string;
          limitDateTime: string;
        };
        configText?: {
          nullable: boolean;
          defaultValue: string;
          minLength: number;
          maxLength: number;
        };
        configEnum?: {
          nullable: boolean;
          defaultValue: string;
          options: string[];
        };
      };
    }[];
    customProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
    }[];
    customConfigurations: {
      key: string;
      name: string;
      description: string;
      type: 'input' | 'select';
      input: string;
      select: string[];
    }[];
    directDependencies: {
      id: string;
      name: string;
      type: 'resource' | 'object';
      versionRange?: string;
    }[];
    baseUpcastResources: {
      resourceID: string;
      resourceName: string;
    }[];
    isCompleteAuthorization: boolean;
    batchSignContracts: {
      resourceID: string;
      resourceName: string;
      policyIDs: string[];
      subjectType: string;
    }[];
  };

  autoGenerateCover?: boolean;

  onChange?(value: CardProps['info']): void;

  onDelete?(): void;

  onClickApplyLabels?(): void;

  onClickApplyPolicies?(): void;
}

function Card({
  order,
  username,
  info,
  resourceType,
  resourceTypeCode,
  autoGenerateCover = false,
  onChange,
  onDelete,
  onClickApplyLabels,
  onClickApplyPolicies,
}: CardProps) {
  const [$showMore, set$showMore, get$showMore] = FUtil.Hook.useGetState<boolean>(false);

  const [$copyInfo, set$copyInfo, get$copyInfo] = FUtil.Hook.useGetState<CardProps['info']>(info);

  React.useEffect(() => {
    set$copyInfo(info);
  }, [info]);

  const { run } = AHooks.useDebounceFn(
    async () => {
      let nameErrorText: string = '';
      if (info.resourceName_optimized === '') {
        nameErrorText =
          // 请输入资源授权标识
          FI18n.i18nNext.tAuto('naming_convention_resource_authid_required');
      } else {
        const params1: Parameters<typeof FServiceAPI.Resource.info>[0] = {
          resourceIdOrName: encodeURIComponent(`${username}/${info.resourceName_optimized}`),
        };
        const { ret, errCode, msg, data: data_info } = await FServiceAPI.Resource.info(params1);

        if (ret !== 0 || errCode !== 0) {
          return fMessage(msg, 'error');
        }

        if (!!data_info) {
          nameErrorText =
            // 资源授权标识已存在
            FI18n.i18nNext.tAuto('resource_name_exist', {
              authID: info.resourceName_optimized,
            });
        }
      }
      onChange?.({
        ...info,
        resourceNameError: nameErrorText,
      });
    },
    {
      wait: 200,
    },
  );

  return (
    <div className={styles.resourceContainer}>
      <div className={styles.resourceOrder}>
        <FComponentsLib.FContentText
          text={FI18n.i18nNext.tAuto('brr_resourcelisting_item_no', {
            ResourceNO: order,
          })}
          type={'highlight'}
          style={{ fontSize: 12 }}
        />
        <FComponentsLib.FTextBtn
          style={{ fontSize: 12 }}
          type={'danger'}
          onClick={() => {
            onDelete && onDelete();
          }}
        >
          <FIcons.FDelete style={{ fontSize: 12 }} />
          &nbsp;{FI18n.i18nNext.tAuto('brr_resourcelisting_item_btn_deleteitem')}
        </FComponentsLib.FTextBtn>
      </div>
      <div style={{ height: 5 }} />
      <div className={styles.whiteCard}>
        <div className={styles.whiteCardLeft}>
          <FCoverImageEditor
            lodding={!info.cover && autoGenerateCover}
            // lodding={true}
            src={info.cover}
            // src={''}
            use={'resource'}
            width={240}
            onChange={(url) => {
              onChange?.({
                ...info,
                cover: url,
              });
            }}
          />
        </div>
        <div className={styles.whiteCardRight}>
          <div className={styles.whiteCardRightRow}>
            <FComponentsLib.FContentText
              text={FI18n.i18nNext.tAuto('brr_resourcelisting_item_filename')}
              type={'negative'}
            />
            <FComponentsLib.FContentText
              text={info.fileName}
              type={'normal'}
              style={{ width: 540, wordBreak: 'break-all' }}
            />
          </div>
          <div style={{ height: 15 }} />
          <div className={styles.whiteCardRightRow}>
            <FComponentsLib.FContentText
              text={FI18n.i18nNext.tAuto('brr_resourcelisting_item_title')}
              type={'negative'}
            />
            <FInput_PinyinSafeTextCounter
              lengthLimit={100}
              value={info.resourceTitle}
              style={{
                width: 540,
                flexShrink: 0,
              }}
              placeholder={FI18n.i18nNext.t('cqr_input_title_hint')}
              onChangeValue={(value) => {
                const resourceName_optimized: string = FRegExpMgr.resourceNameOptimized(
                  value.substring(0, 60),
                );
                onChange?.({
                  ...info,
                  resourceTitle: value,
                  resourceTitleError:
                    value === ''
                      ? // ? '请输入标题'
                        FI18n.i18nNext.tAuto('naming_convention_resource_title_required')
                      : value.length > 100
                      ? '不超过100个字符'
                      : '',
                  resourceName:
                    info.resourceTitle === info.resourceName
                      ? value.substring(0, 60)
                      : info.resourceName,
                  resourceName_optimized:
                    info.resourceTitle === info.resourceName
                      ? resourceName_optimized
                      : info.resourceName_optimized,
                });
                run();
              }}
              hasError={info.resourceTitleError !== ''}
            />
          </div>

          {info.resourceTitleError !== '' && (
            <>
              <div style={{ height: 5 }} />
              <div style={{ color: '#EE4040', fontSize: 12 }}>{info.resourceTitleError}</div>
            </>
          )}
          <div style={{ height: 15 }} />

          {/* 资源名称 */}
          <ResourceNameInput
            username={username}
            resourceName={info.resourceName}
            resourceName_optimized={info.resourceName_optimized}
            resourceNameError={info.resourceNameError}
            onChange={(value) => {
              onChange?.({
                ...info,
                ...value,
              });
            }}
          />
          {/* 资源名称 */}

          <div style={{ height: 15 }} />

          <div className={styles.whiteCardRightRow} style={{ alignItems: 'flex-start' }}>
            <FComponentsLib.FContentText
              text={FI18n.i18nNext.tAuto('brr_resourcelisting_item_tag')}
              type={'negative'}
              style={{
                transform: 'translateY(8px)',
              }}
            />

            <ResourceLabelsCard
              style={{ width: 540 }}
              labels={info.resourceLabels}
              onClick={async () => {
                const result: null | string[] = await fEditLabelsDrawer({
                  labels: info.resourceLabels,
                  resourceTypeCode: resourceTypeCode,
                });
                if (result === null) {
                  return;
                }

                onChange?.({
                  ...info,
                  resourceLabels: result,
                });
              }}
            />
          </div>
          {onClickApplyLabels && (
            <>
              <div style={{ height: 5 }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <FHotspotTooltip
                  zIndex={100}
                  id={'batchResourceCreator.perfectInformation.applyLabelButton'}
                  style={{ left: 26, bottom: -38 }}
                  text={FI18n.i18nNext.tAuto('hotpots_brr_info_tagadded')}
                  onOpenChange={(o) => {
                    if (o) {
                      fSetHotspotTooltipVisible(
                        'batchResourceCreator.perfectInformation.applyLabelButton',
                        {
                          value: false,
                          effectiveImmediately: false,
                          onlyNullish: false,
                        },
                      );
                    }
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                  >
                    <FComponentsLib.FTextBtn>
                      <FIcons.FApply />
                    </FComponentsLib.FTextBtn>
                    <FComponentsLib.FTextBtn
                      style={{ fontSize: 12 }}
                      type={'primary'}
                      onClick={() => {
                        fSetHotspotTooltipVisible(
                          'batchResourceCreator.perfectInformation.applyLabelButton',
                          {
                            value: false,
                            effectiveImmediately: true,
                            onlyNullish: false,
                          },
                        );
                        onClickApplyLabels?.();
                      }}
                    >
                      {FI18n.i18nNext.tAuto('brr_resourcelisting_item_btn_bulkaddtag')}
                    </FComponentsLib.FTextBtn>
                  </div>
                </FHotspotTooltip>
              </div>
            </>
          )}

          <div style={{ height: 15 }} />

          <div className={styles.whiteCardRightRow} style={{ alignItems: 'flex-start' }}>
            <FComponentsLib.FContentText
              text={FI18n.i18nNext.tAuto('brr_resourcelisting_item_authplan')}
              type={'negative'}
            />
            <div style={{ width: 540 }}>
              <Space size={15}>
                <Space
                  size={5}
                  onClick={async () => {
                    const result: null | { title: string; text: string } = await fPolicyBuilder3({
                      // targetType: 'resource',
                      alreadyUsedTexts: info.resourcePolicies.map((p) => {
                        return p.text;
                      }),
                      alreadyUsedTitles: info.resourcePolicies.map((p) => {
                        return p.title;
                      }),
                      resourceTypeCode: [resourceTypeCode || ''],
                    });
                    if (!result) {
                      return;
                    }
                    onChange?.({
                      ...info,
                      resourcePolicies: [...info.resourcePolicies, result],
                    });
                  }}
                >
                  <FComponentsLib.FTextBtn>
                    <FIcons.FCreate />
                  </FComponentsLib.FTextBtn>
                  <FComponentsLib.FTextBtn>
                    {FI18n.i18nNext.tAuto('brr_resourcelisting_item_btn_addauthplan')}
                  </FComponentsLib.FTextBtn>
                </Space>
                {onClickApplyPolicies && (
                  <FHotspotTooltip
                    zIndex={100}
                    id={'batchResourceCreator.perfectInformation.applyPolicyButton'}
                    style={{ left: 26, bottom: -38 }}
                    text={FI18n.i18nNext.tAuto('hotpots_brr_info_planadded')}
                    onOpenChange={(o) => {
                      if (o) {
                        fSetHotspotTooltipVisible(
                          'batchResourceCreator.perfectInformation.applyPolicyButton',
                          {
                            value: false,
                            effectiveImmediately: false,
                            onlyNullish: false,
                          },
                        );
                      }
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <FComponentsLib.FTextBtn>
                        <FIcons.FApply />
                      </FComponentsLib.FTextBtn>
                      <FComponentsLib.FTextBtn
                        type={'primary'}
                        onClick={() => {
                          fSetHotspotTooltipVisible(
                            'batchResourceCreator.perfectInformation.applyPolicyButton',
                            {
                              value: false,
                              effectiveImmediately: true,
                              onlyNullish: false,
                            },
                          );
                          onClickApplyPolicies();
                        }}
                      >
                        应用于所有资源
                      </FComponentsLib.FTextBtn>
                    </div>
                  </FHotspotTooltip>
                )}
              </Space>
              {info.resourcePolicies.length > 0 && (
                <>
                  <div style={{ height: 15 }} />
                  <div className={styles.policies}>
                    {info.resourcePolicies.map((p) => {
                      return (
                        <label className={styles.policy} key={p.title}>
                          <span>{p.title}</span>
                          <a
                            onClick={() => {
                              onChange?.({
                                ...info,
                                resourcePolicies: info.resourcePolicies.filter((l) => {
                                  return l.title !== p.title;
                                }),
                              });
                            }}
                          >
                            <FIcons.FClose style={{ fontSize: 8 }} />
                          </a>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: 10 }} />
      <Space size={10}>
        <FComponentsLib.FTextBtn
          style={{ fontSize: 12 }}
          onClick={() => {
            set$showMore(!get$showMore());
          }}
        >
          {$showMore
            ? FI18n.i18nNext.tAuto('brr_resourcelisting_item_btn_showlesssetting')
            : FI18n.i18nNext.tAuto('brr_resourcelisting_item_btn_moresetting')}
        </FComponentsLib.FTextBtn>
        <FComponentsLib.FContentText
          text={FI18n.i18nNext.tAuto('brr_resourcelisting_item_btn_moresetting_msg')}
          type={'negative'}
          style={{ fontSize: 12 }}
        />
      </Space>

      {$showMore && (
        <>
          <div style={{ height: 5 }} />
          <div className={styles.block}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <FComponentsLib.FContentText
                text={
                  // 基础属性
                  FI18n.i18nNext.tAuto('resourceinfo_title')
                }
                type={'highlight'}
              />
              {info.customProperties.length < 30 && (
                <FTooltip title={FI18n.i18nNext.tAuto('resourceinfo_add_btn_info')}>
                  <div>
                    <FComponentsLib.FTextBtn
                      style={{
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                      type='primary'
                      onClick={async () => {
                        const dataSource: {
                          key: string;
                          name: string;
                          description: string;
                        } | null = await fResourcePropertyEditor3({
                          disabledKeys: [
                            ...info.systemProperties.map<string>((sp) => sp.key),
                            ...info.customProperties.map<string>((bp) => bp.key),
                            ...info.customConfigurations.map<string>((pp) => pp.key),
                          ],
                          disabledNames: [
                            ...info.systemProperties.map<string>((sp) => sp.name),
                            ...info.customProperties.map<string>((bp) => bp.name),
                            ...info.customConfigurations.map<string>((pp) => pp.name),
                          ],
                        });
                        if (!dataSource) {
                          return;
                        }
                        onChange?.({
                          ...info,
                          customProperties: [
                            ...info.customProperties,
                            {
                              ...dataSource,
                              value: '',
                            },
                          ],
                        });
                      }}
                    >
                      <FIcons.FProperty style={{ fontSize: 14 }} />
                      <span>{FI18n.i18nNext.tAuto('resourceinfo_add_btn')}</span>
                    </FComponentsLib.FTextBtn>
                  </div>
                </FTooltip>
              )}
            </div>

            {info.systemProperties.length === 0 && info.customProperties.length === 0 && (
              <>
                <div style={{ height: 10 }} />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FComponentsLib.FContentText
                    text={
                      // 暂无数据
                      FI18n.i18nNext.tAuto('resourceinfo_empty_msg')
                    }
                    type={'additional2'}
                    style={{ maxWidth: 600, textAlign: 'center' }}
                  />
                </div>
                <div style={{ height: 20 }} />
              </>
            )}

            {!(info.systemProperties.length === 0 && info.customProperties.length === 0) && (
              <>
                <div style={{ height: 20 }} />
                <FAttrsAndConfigs
                  dateSource={[
                    ...info.systemProperties.map<IAttrsAndConfigsItem>((sp) => {
                      return {
                        key: sp.key,
                        value: sp.value,
                        name: sp.name,
                        description: sp.description,
                        configs: {
                          item_Deletable: false,
                          key_Editable: false,
                          name_Editable: false,
                          description_Editable: false,
                          value_Editable: (sp.type === 'additional' && sp.valueConfig) || false,
                        },
                      };
                    }),
                    ...info.customProperties.map<IAttrsAndConfigsItem>((cp) => {
                      return {
                        key: cp.key,
                        value: cp.value,
                        name: cp.name,
                        description: cp.description,
                        configs: {
                          item_Deletable: true,
                          key_Editable: true,
                          name_Editable: true,
                          description_Editable: true,
                          value_Editable: {
                            text: {
                              nullable: true,
                              minLength: 0,
                              maxLength: 100,
                            },
                          },
                        },
                      };
                    }),
                  ]}
                  onChangeItem={(oldData, newData, index) => {
                    if (index < info.systemProperties.length) {
                      const newDateSource = [...info.systemProperties];
                      newDateSource[index] = {
                        key: newData.key,
                        name: newData.name,
                        value: newData.value,
                        description: newData.description,
                        type: info.systemProperties[index].type,
                        valueConfig: info.systemProperties[index].valueConfig,
                      };

                      onChange?.({
                        ...info,
                        systemProperties: newDateSource,
                      });
                    } else {
                      const newDateSource = [...info.customProperties];
                      newDateSource[index - info.systemProperties.length] = {
                        key: newData.key,
                        name: newData.name,
                        value: newData.value,
                        description: newData.description,
                      };
                      onChange?.({
                        ...info,
                        customProperties: newDateSource,
                      });
                    }
                  }}
                  onDeleteItem={(value) => {
                    onChange?.({
                      ...info,
                      customProperties: info.customProperties.filter((v) => {
                        return v.key !== value.key && v.name !== value.name;
                      }),
                    });
                  }}
                  disabledKeys={[
                    ...info.systemProperties.map<string>((rp) => rp.key),
                    ...info.customProperties.map<string>((cp) => cp.key),
                    ...info.customConfigurations.map<string>((cp) => cp.key),
                  ]}
                  disabledNames={[
                    ...info.systemProperties.map<string>((rp) => rp.name),
                    ...info.customProperties.map<string>((cp) => cp.name),
                    ...info.customConfigurations.map<string>((cp) => cp.name),
                  ]}
                />
              </>
            )}
          </div>

          {(resourceType.includes('插件') || resourceType.includes('主题')) && (
            <>
              <div style={{ height: 5 }} />
              <div className={styles.block}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <FComponentsLib.FContentText
                    text={FI18n.i18nNext.tAuto('resourceoptions_title')}
                    type={'highlight'}
                  />
                  {info.customConfigurations.length < 30 && (
                    <FTooltip title={FI18n.i18nNext.tAuto('info_versionoptions')}>
                      <div>
                        <FComponentsLib.FTextBtn
                          style={{
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5,
                          }}
                          type='primary'
                          onClick={async () => {
                            const dataSource: {
                              key: string;
                              name: string;
                              type: 'input' | 'select';
                              input: string;
                              select: string[];
                              description: string;
                            } | null = await fResourceOptionEditor({
                              disabledKeys: [
                                ...info.systemProperties.map<string>((sp) => sp.key),
                                ...info.customProperties.map<string>((bp) => bp.key),
                                ...info.customConfigurations.map<string>((pp) => pp.key),
                              ],
                              disabledNames: [
                                ...info.systemProperties.map<string>((sp) => sp.name),
                                ...info.customProperties.map<string>((bp) => bp.name),
                                ...info.customConfigurations.map<string>((pp) => pp.name),
                              ],
                            });

                            if (!dataSource) {
                              return;
                            }

                            onChange?.({
                              ...info,
                              customConfigurations: [...info.customConfigurations, dataSource],
                            });
                          }}
                        >
                          <FIcons.FConfiguration style={{ fontSize: 14 }} />
                          <span>{FI18n.i18nNext.tAuto('resourceoptions_add_btn')}</span>
                        </FComponentsLib.FTextBtn>
                      </div>
                    </FTooltip>
                  )}
                </div>

                {info.customConfigurations.length === 0 && (
                  <>
                    <div style={{ height: 10 }} />
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FComponentsLib.FContentText
                        text={FI18n.i18nNext.tAuto('resourceoptions_list_empty')}
                        type={'additional2'}
                      />
                    </div>
                    <div style={{ height: 20 }} />
                  </>
                )}

                {info.customConfigurations.length > 0 && (
                  <>
                    <div style={{ height: 20 }} />
                    <FResourceOptions
                      theme={'dark'}
                      dataSource={info.customConfigurations}
                      onEdit={async (value) => {
                        const index: number = info.customConfigurations.findIndex((p) => {
                          return p === value;
                        });

                        const dataSource: {
                          key: string;
                          name: string;
                          type: 'input' | 'select';
                          input: string;
                          select: string[];
                          description: string;
                        } | null = await fResourceOptionEditor({
                          disabledKeys: [
                            ...info.systemProperties.map<string>((sp) => sp.key),
                            ...info.customProperties.map<string>((bp) => bp.key),
                            ...info.customConfigurations.map<string>((pp) => pp.key),
                          ],
                          disabledNames: [
                            ...info.systemProperties.map<string>((sp) => sp.name),
                            ...info.customProperties.map<string>((bp) => bp.name),
                            ...info.customConfigurations.map<string>((pp) => pp.name),
                          ],
                          defaultData: value,
                        });

                        if (!dataSource) {
                          return;
                        }

                        onChange?.({
                          ...info,
                          customConfigurations: info.customConfigurations.map((a, b) => {
                            if (b !== index) {
                              return a;
                            }
                            return dataSource;
                          }),
                        });
                      }}
                      onDelete={async (value) => {
                        onChange?.({
                          ...info,
                          customConfigurations: info.customConfigurations.filter((a) => {
                            return a.key !== value.key && a.name !== value.name;
                          }),
                        });
                      }}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}

      <div style={{ display: $showMore ? 'block' : 'none' }}>
        <div style={{ height: 5 }} />
        <div className={styles.block}>
          <FMicroAPP_Authorization
            licenseeId={''}
            mainAppType={'resourceInBatchPublish'}
            depList={info.directDependencies}
            upcastList={info.baseUpcastResources}
            update={(data) => {
              onChange?.({
                ...get$copyInfo(),
                directDependencies: data.directDependencies || [],
                baseUpcastResources: data.baseUpcastResources || [],
                isCompleteAuthorization: data.isAllAuthComplete || false,
                batchSignContracts: data.batchSignContracts || [],
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default Card;

interface ResourceNameInputProps {
  username: string;
  resourceName: string;
  resourceName_optimized: string;
  resourceNameError: string;

  onChange(value: {
    resourceName: string;
    resourceName_optimized: string;
    resourceNameError: string;
  }): void;
}

function ResourceNameInput({
  username,
  resourceName,
  resourceName_optimized,
  resourceNameError,
  onChange,
}: ResourceNameInputProps) {
  const [$resourceName, set$resourceName, get$resourceName] =
    FUtil.Hook.useGetState<string>(resourceName);
  const [$resourceName_optimized, set$resourceName_optimized, get$resourceName_optimized] =
    FUtil.Hook.useGetState<string>(resourceName_optimized);
  const [$resourceNameError, set$resourceNameError, get$resourceNameError] =
    FUtil.Hook.useGetState<string>(resourceNameError);

  AHooks.useDebounceEffect(
    () => {
      if (resourceName !== get$resourceName()) {
        set$resourceName(resourceName);
      }
    },
    [resourceName],
    {
      wait: 100,
    },
  );

  AHooks.useDebounceEffect(
    () => {
      if (resourceName_optimized !== get$resourceName_optimized()) {
        set$resourceName_optimized(resourceName_optimized);
      }
    },
    [resourceName_optimized],
    {
      wait: 100,
    },
  );
  AHooks.useDebounceEffect(
    () => {
      if (resourceNameError !== get$resourceNameError()) {
        set$resourceNameError(resourceNameError);
      }
    },
    [resourceNameError],
    {
      wait: 100,
    },
  );

  const { run } = AHooks.useDebounceFn(
    async () => {
      let nameErrorText: string = '';
      if (get$resourceName_optimized() === '') {
        nameErrorText =
          // 请输入资源授权标识
          FI18n.i18nNext.tAuto('naming_convention_resource_authid_required');
      } else if (get$resourceName_optimized().length > 60) {
        nameErrorText = '不多于60个字符';
      } else {
        const params1: Parameters<typeof FServiceAPI.Resource.info>[0] = {
          resourceIdOrName: encodeURIComponent(`${username}/${get$resourceName_optimized()}`),
        };
        const { data: data_info } = await FServiceAPI.Resource.info(params1);
        if (!!data_info) {
          nameErrorText = FI18n.i18nNext.tAuto('resource_name_exist', {
            authID: get$resourceName_optimized(),
          });
        }
      }
      set$resourceNameError(nameErrorText);
      onChange({
        resourceName: get$resourceName(),
        resourceName_optimized: get$resourceName_optimized(),
        resourceNameError: get$resourceNameError(),
      });
    },
    {
      wait: 300,
    },
  );

  return (
    <>
      <div className={styles.whiteCardRightRow}>
        <FComponentsLib.FContentText
          text={FI18n.i18nNext.tAuto('brr_resourcelisting_item_authid')}
          type={'negative'}
        />

        <FInput_PinyinSafeTextCounter
          style={{ width: 540, flexShrink: 0 }}
          lengthLimit={60}
          prefix={
            <span
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: '#BEBEBE',
                lineHeight: '20px',
              }}
            >
              {username}&nbsp;/&nbsp;
            </span>
          }
          value={$resourceName}
          onChangeValue={(value) => {
            set$resourceName(value);
            set$resourceName_optimized(FRegExpMgr.resourceNameOptimized(value));
            run();
          }}
          placeholder={FI18n.i18nNext.t('rqr_input_resourceauthid_hint')}
          hasError={$resourceNameError !== ''}
        />
      </div>

      {$resourceNameError === '' && $resourceName !== $resourceName_optimized && (
        <>
          <div style={{ height: 5 }} />
          <div>
            <div
              style={{
                color: '#44C28C',
                fontSize: 12,
                lineHeight: '18px',
              }}
            >
              {/* 你的资源标识将自动转换为 <strong>{$resourceName_optimized}</strong> */}
              {
                // 您的资源授权标识将自动转换为{authid}
                FI18n.i18nNext.tAuto('input_resourceauthid_automodified_msg', {
                  authid: $resourceName_optimized,
                })
              }
            </div>
            <div style={{ height: 5 }} />
            <div
              style={{
                color: '#E9A923',
                fontSize: 12,
                lineHeight: '18px',
              }}
            >
              {FI18n.i18nNext.tAuto('naming_convention_resource_name')}
            </div>
          </div>
        </>
      )}

      {$resourceNameError !== '' && (
        <>
          <div style={{ height: 5 }} />
          <div style={{ color: '#EE4040', fontSize: 12 }}>{$resourceNameError}</div>
        </>
      )}
    </>
  );
}
